import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel } from "../../components/Bits";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { Receipt, Wrench, CalendarRange, Megaphone, ArrowRight, AlertTriangle } from "lucide-react";
import { useAuth } from "../../lib/auth";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/student/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <AppShell>
      <PageHeader
        overline={`hello, ${user.name?.split(" ")[0] || "resident"}`}
        title="Welcome home."
        subtitle="Everything you need at Cozy Heaven, in one quiet place."
      />

      {stats === null ? (
        <div className="grid sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 rounded-lg border border-warm bg-white animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4 mb-10">
          <Stat label="Fees paid" value={`₹ ${stats.fees_paid.toLocaleString()}`} tint="bg-[#DCE3D6] text-[#4A5D3E]" icon={Receipt} />
          <Stat label="Fees pending" value={`₹ ${stats.fees_pending.toLocaleString()}`} tint="bg-[#FBEED7] text-[#8C6D31]" icon={Receipt} />
          <Stat label="Pending fines" value={`₹ ${(stats.pending_fines || 0).toLocaleString()}`} tint="bg-[#F5D8D8] text-[#8C3F3F]" icon={AlertTriangle} />
          <Stat label="Open tickets" value={stats.open_tickets} tint="bg-[#D6E6EB] text-[#3D5C68]" icon={Wrench} />
          <Stat label="Pending leaves" value={stats.pending_leaves} tint="bg-[#E8C2B8] text-[#8C3F2C]" icon={CalendarRange} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Recent notices" className="lg:col-span-2">
          {stats?.recent_notices?.length ? (
            <ul className="divide-y divide-[#EAE5DE]">
              {stats.recent_notices.map((n) => (
                <li key={n.id} className="py-3">
                  <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#A67B5B]" /><span className="text-xs uppercase tracking-wider text-warm">{n.priority}</span></div>
                  <div className="font-medium text-deep mt-1">{n.title}</div>
                  <div className="text-sm text-warm line-clamp-2">{n.content}</div>
                </li>
              ))}
            </ul>
          ) : <div className="text-sm text-warm">No notices yet.</div>}
          <Link to="/student/notices" className="inline-flex items-center gap-1 text-sm text-[#C86F58] mt-4 hover:underline">See all <ArrowRight className="h-3 w-3" /></Link>
        </Panel>

        <Panel title="Quick actions">
          <div className="space-y-2">
            <QuickAction to="/student/tickets" label="Raise a ticket" desc="Fan, light, water cooler or anything else." />
            <QuickAction to="/student/leaves" label="Apply for leave" desc="Going home? Submit a leave/day-out form." />
            <QuickAction to="/student/fees" label="My fee records" desc="Download payment receipts." />
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, tint, icon: Icon }) {
  return (
    <div className="bg-white border border-warm rounded-lg p-5 card-lift">
      <div className={`inline-flex h-9 w-9 rounded-md items-center justify-center ${tint}`}><Icon className="h-4 w-4" /></div>
      <div className="mt-3 font-body font-semibold text-3xl text-deep tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-warm mt-1">{label}</div>
    </div>
  );
}

function QuickAction({ to, label, desc }) {
  return (
    <Link to={to} className="block border border-warm rounded-md p-3 hover:bg-[#FDFBF7] transition-colors group" data-testid={`quick-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="font-medium text-deep">{label}</div>
        <ArrowRight className="h-4 w-4 text-warm group-hover:text-[#C86F58]" />
      </div>
      <div className="text-xs text-warm mt-1">{desc}</div>
    </Link>
  );
}
